import React, { useEffect, useState } from 'react';
import Icon from './AppIcon';

const TOAST_ICONS = {
  badge: { name: 'Award', color: '#D4AF37' },
  streak: { name: 'Flame', color: '#F97316' },
  achievement: { name: 'Star', color: '#A855F7' },
  success: { name: 'CheckCircle2', color: '#22C55E' },
  info: { name: 'Info', color: '#60A5FA' },
};

const TOAST_STYLES = {
  badge: 'border-amber-500/40 bg-amber-950/90',
  streak: 'border-orange-500/40 bg-orange-950/90',
  achievement: 'border-purple-500/40 bg-purple-950/90',
  success: 'border-green-500/40 bg-green-950/90',
  info: 'border-blue-500/40 bg-blue-950/90',
};

const PROGRESS_COLORS = {
  badge: 'bg-amber-400',
  streak: 'bg-orange-400',
  achievement: 'bg-purple-400',
  success: 'bg-green-400',
  info: 'bg-blue-400',
};

const Toast = ({ toast, onDismiss }) => {
  const [visible, setVisible] = useState(false);
  const [progress, setProgress] = useState(100);
  const duration = 4500;

  useEffect(() => {
    // Trigger enter animation
    const enterTimer = setTimeout(() => setVisible(true), 20);

    // Shrink progress bar
    const start = Date.now();
    const tick = () => {
      const elapsed = Date.now() - start;
      const remaining = Math.max(0, 100 - (elapsed / duration) * 100);
      setProgress(remaining);
      if (remaining > 0) requestAnimationFrame(tick);
    };
    const rafId = requestAnimationFrame(tick);

    return () => {
      clearTimeout(enterTimer);
      cancelAnimationFrame(rafId);
    };
  }, []);

  const handleDismiss = () => {
    setVisible(false);
    setTimeout(() => onDismiss(toast?.id), 300);
  };

  const icon = TOAST_ICONS?.[toast?.type] || TOAST_ICONS?.info;
  const styleClass = TOAST_STYLES?.[toast?.type] || TOAST_STYLES?.info;
  const progressClass = PROGRESS_COLORS?.[toast?.type] || PROGRESS_COLORS?.info;

  return (
    <div
      role="alert"
      aria-live="polite"
      onClick={handleDismiss}
      className={`
        relative w-full max-w-sm rounded-xl border shadow-2xl backdrop-blur-md
        cursor-pointer select-none overflow-hidden
        transition-all duration-300 ease-out
        ${styleClass}
        ${visible ? 'opacity-100 translate-x-0' : 'opacity-0 translate-x-8'}
      `}
    >
      {/* Content */}
      <div className="flex items-start gap-3 px-4 pt-4 pb-3">
        <div className="flex-shrink-0 mt-0.5">
          <Icon name={icon?.name} size={22} color={icon?.color} />
        </div>
        <div className="flex-1 min-w-0">
          <p className="text-sm font-bold text-white leading-tight">{toast?.title}</p>
          <p className="text-xs text-white/75 mt-0.5 leading-snug">{toast?.message}</p>
        </div>
        <button
          onClick={(e) => { e?.stopPropagation(); handleDismiss(); }}
          className="flex-shrink-0 text-white/40 hover:text-white/80 transition-colors mt-0.5"
          aria-label="Dismiss notification"
        >
          <Icon name="X" size={14} />
        </button>
      </div>
      {/* Progress bar */}
      <div className="h-0.5 w-full bg-white/10">
        <div
          className={`h-full ${progressClass} transition-none`}
          style={{ width: `${progress}%` }}
        />
      </div>
    </div>
  );
};

const ToastContainer = ({ toasts, onDismiss }) => {
  if (!toasts?.length) return null;

  return (
    <div
      className="fixed bottom-6 right-4 sm:right-6 z-[9999] flex flex-col gap-3 items-end pointer-events-none"
      aria-label="Notifications"
    >
      {toasts?.map((toast) => (
        <div key={toast?.id} className="pointer-events-auto w-full max-w-sm">
          <Toast toast={toast} onDismiss={onDismiss} />
        </div>
      ))}
    </div>
  );
};

export default ToastContainer;
